import { useState } from "react";
import { ArrowRight, Check } from "lucide-react";
import PageHeader from "@/components/PageHeader";
import { api, money } from "@/lib/api";

export default function TrackOrder() {
  const [orderNumber, setOrderNumber] = useState("");
  const [email, setEmail] = useState("");
  const [order, setOrder] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    setOrder(null);
    try {
      const data = await api.trackOrder(orderNumber, email);
      setOrder(data);
    } catch {
      setError("NO ORDER FOUND FOR THAT ORDER NUMBER AND EMAIL.");
    } finally {
      setLoading(false);
    }
  };

  const stages = ["ORDER CONFIRMED", "PREPARING", "SHIPPED", "DELIVERED"];
  const activeStage = order?.status === "paid" ? 1 : 0;

  return (
    <div data-testid="track-order-page">
      <PageHeader overline="ORDER STATUS" title="Track Order" sub="Enter the order number from your confirmation (DR-XXXXXX) and the email used at checkout." />
      <div className="mx-auto max-w-[1100px] px-5 sm:px-8 py-14">
        <form onSubmit={submit} className="grid grid-cols-1 gap-3 sm:grid-cols-3" data-testid="track-form">
          <input
            data-testid="track-order-number-input"
            value={orderNumber}
            onChange={(e) => setOrderNumber(e.target.value)}
            placeholder="ORDER NUMBER (DR-XXXXXX)"
            required
            className="border border-wire bg-transparent px-5 py-4 font-mono text-xs tracking-[0.2em] text-white placeholder:text-neutral-600 focus:border-white focus:outline-none"
          />
          <input
            data-testid="track-email-input"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="EMAIL ADDRESS"
            required
            className="border border-wire bg-transparent px-5 py-4 font-mono text-xs tracking-[0.2em] text-white placeholder:text-neutral-600 focus:border-white focus:outline-none"
          />
          <button data-testid="track-submit-button" disabled={loading}
            className="flex items-center justify-center gap-3 bg-white px-8 py-4 font-mono text-[11px] tracking-[0.35em] text-black transition-colors hover:bg-neutral-300 disabled:opacity-60">
            {loading ? "SEARCHING..." : <>TRACK <ArrowRight size={13} /></>}
          </button>
        </form>

        {error && (
          <p className="mt-8 border border-wire px-6 py-5 font-mono text-xs tracking-[0.25em] text-neutral-400" data-testid="track-error">
            {error}
          </p>
        )}

        {order && (
          <div className="mt-12" data-testid="track-result">
            <div className="grid grid-cols-1 gap-px border border-wire bg-wire sm:grid-cols-4">
              {stages.map((stage, i) => (
                <div key={stage} className={`p-6 ${i <= activeStage ? "bg-charcoal" : "bg-obsidian"}`} data-testid={`track-stage-${i}`}>
                  <div className={`flex h-6 w-6 items-center justify-center border ${i <= activeStage ? "border-white bg-white text-black" : "border-wire text-transparent"}`}>
                    <Check size={12} />
                  </div>
                  <p className={`mt-4 font-mono text-[10px] tracking-[0.3em] ${i <= activeStage ? "text-white" : "text-neutral-600"}`}>{stage}</p>
                </div>
              ))}
            </div>
            <div className="border border-t-0 border-wire bg-obsidian p-8">
              <div className="flex flex-wrap items-baseline justify-between gap-4">
                <p className="font-mono text-lg tracking-[0.15em] text-white" data-testid="track-order-number">{order.order_number}</p>
                <p className="font-mono text-[10px] tracking-[0.3em] text-neutral-500">ESTIMATED DELIVERY — {order.estimated_delivery || "5–7 BUSINESS DAYS"}</p>
              </div>
              <div className="mt-6 divide-y divide-wire border-t border-wire">
                {order.items.map((item, i) => (
                  <div key={i} className="flex items-center justify-between py-4">
                    <div>
                      <p className="font-display text-sm font-bold uppercase text-white">{item.name}</p>
                      <p className="mt-1 font-mono text-[10px] tracking-[0.25em] text-neutral-500">SIZE {item.size} — QTY {item.qty}</p>
                    </div>
                    <p className="font-mono text-sm text-neutral-200">{money(item.price * item.qty)}</p>
                  </div>
                ))}
              </div>
              <p className="mt-6 font-mono text-[10px] tracking-[0.2em] text-neutral-500">
                TRACKING NUMBER WILL APPEAR HERE AND BE EMAILED ONCE YOUR ORDER SHIPS.
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
